import type { AgentRun } from '@/api/agentRuns'
import type { AIChatMessage } from '@/stores/aiChatTypes'
import type { AgentApprovalResponse } from '@/types'

export type TimelineEntry =
  | { kind: 'message'; key: string; message: AIChatMessage }
  | { kind: 'approval'; key: string; approval: AgentApprovalResponse }
  | { kind: 'run'; key: string; run: AgentRun }

function entryTime(entry: TimelineEntry): number {
  if (entry.kind === 'message') return Date.parse(entry.message.timestamp)
  if (entry.kind === 'approval') return Date.parse(entry.approval.created_at)
  return Date.parse(entry.run.started_at)
}

/** The index right after the message a run answered (by its server id), or -1. */
function afterAnsweredMessage(entries: TimelineEntry[], run: AgentRun): number {
  const answered = new Set(run.message_ids)
  const at = entries.findIndex(
    (e) =>
      e.kind === 'message' &&
      (answered.has(e.message.id) || answered.has(e.message.metadata?.backend_message_id ?? ''))
  )
  if (at < 0) return -1
  let index = at + 1
  while (index < entries.length && entries[index].kind === 'run') index += 1
  return index
}

/**
 * Messages (already in conversation order) with each pending approval placed where it
 * happened: after the last entry at or before its time. Each agent run's activity row sits
 * right after the message it answered (before its reply), else by when it started.
 */
export function buildTimeline(
  messages: AIChatMessage[],
  approvals: AgentApprovalResponse[],
  runs: AgentRun[] = []
): TimelineEntry[] {
  const entries: TimelineEntry[] = messages.map((message) => ({
    kind: 'message',
    key: message.id,
    message,
  }))
  const anchored: TimelineEntry[] = [
    ...approvals.map(
      (approval): TimelineEntry => ({ kind: 'approval', key: `approval-${approval.id}`, approval })
    ),
    ...runs.map((run): TimelineEntry => ({ kind: 'run', key: `run-${run.id}`, run })),
  ].sort((a, b) => entryTime(a) - entryTime(b))
  for (const item of anchored) {
    const answered = item.kind === 'run' ? afterAnsweredMessage(entries, item.run) : -1
    if (answered >= 0) {
      entries.splice(answered, 0, item)
      continue
    }
    const at = entryTime(item)
    let index = entries.length
    while (index > 0 && entryTime(entries[index - 1]) > at) index -= 1
    entries.splice(index, 0, item)
  }
  return entries
}
